import { Box, Tooltip, Typography } from '@material-ui/core';
import { Backdrop } from '@room-match/shared-ui-components';
import AvatarSkeleton from 'components/Avatar/AvatarSkeleton/AvatarSkeleton';
import { FC, Fragment, memo } from 'react';
import { useQueryClient } from 'react-query';
import { PROFILE_LINKS_QUERY_STRING } from 'shared/constants/QUERY_STRING';
import { Switch } from 'shared/elements/Switch';
import useImageLoader from 'shared/hooks/useImageLoader';
import { IProfileLink } from 'shared/interfaces/IProfile';
import { profileService } from 'shared/services/profileService';
import { nonRepLabel } from 'shared/utils/nonRepLabel';
import { ProfileImage, ProfileImageContainer, ProfileListMeContainer } from './ProfileListMe.styles';
const { updateProfile } = profileService();

type ProfileListMeProps = {
  profileLink: IProfileLink;
  isPublished: boolean;
  setIsPublished: Function;
  selectedPrimary: string | null;
  country_code?: string;
};

const ProfileListMe: FC<ProfileListMeProps> = ({
  profileLink,
  isPublished,
  setIsPublished,
  selectedPrimary,
  country_code,
}) => {
  const queryClient = useQueryClient();
  const { primary_medium_attachment, representation_type, agency } = profileLink;
  const { mutate, isLoading: isUpdating } = updateProfile();
  const image = primary_medium_attachment?.modified_attachment;
  const isRepresented = representation_type === 'agency_representation' ? true : false;
  const { imageLoaded, noProfileImage, error } = useImageLoader(image);

  const handlePublished = () => {
    mutate(
      {
        profileId: profileLink?.id,
        payload: {
          is_published: !isPublished,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries([PROFILE_LINKS_QUERY_STRING.PROFILE_LIST]);
        },
      },
    );
    setIsPublished((prev: boolean) => !prev);
  };

  return (
    <ProfileListMeContainer>
      <Backdrop isLoading={isUpdating} />
      <ProfileImageContainer>
        {imageLoaded ? (
          <ProfileImage src={imageLoaded && !error ? image : noProfileImage} alt="" loading="lazy" />
        ) : (
          <AvatarSkeleton style={{ width: '100%', height: '100%', border: 'none' }} />
        )}
      </ProfileImageContainer>
      <Box alignSelf={'end'}>
        <Typography style={{ fontSize: 14, wordBreak: 'break-word' }}>
          {isRepresented ? agency?.full_name : nonRepLabel(country_code || 'us')}
        </Typography>
        {profileLink?.division ? (
          <Typography color="primary" style={{ fontSize: 12, wordBreak: 'break-word', fontWeight: 700 }}>
            {profileLink?.division?.name}
          </Typography>
        ) : (
          <Fragment />
        )}
        <Tooltip
          title={profileLink?.is_published_by_agency ? 'Agent-Activated' : 'You cannot deactivate a primary link'}
          disableHoverListener={
            (selectedPrimary !== profileLink?.id && !profileLink.is_published_by_agency) ||
            (!profileLink.is_published_by_agency && selectedPrimary !== profileLink?.id)
          }
        >
          <Box style={{ display: 'flex', marginLeft: -18, alignItems: 'center' }}>
            <Switch
              size="small"
              toggle={handlePublished}
              checked={isPublished}
              disabled={
                profileLink?.is_published_by_agency ||
                (selectedPrimary === profileLink?.id && isPublished) ||
                isUpdating
              }
            />
            <Typography style={{ fontSize: 12 }}>{isPublished ? 'Active' : 'Inactive'}</Typography>
          </Box>
        </Tooltip>
      </Box>
    </ProfileListMeContainer>
  );
};

export default memo(ProfileListMe);
